import {ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, OnInit, Output, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {SessionService} from '../../../core/services/session.service';
import {Store} from '@ngrx/store';
import {AppState} from '../../../app/app.state';
import {SubtitleItem, TitleState} from '../../../core/state/title.state';
import {Title} from '@angular/platform-browser';
import {TitleBreadcrumb} from '../../../shared/models/title';
import {AppConfigLocation} from '../../../shared/models/app';
import {ActivatedRoute, NavigationEnd, Router, RouterLink} from '@angular/router';
import {filter, take} from 'rxjs/operators';
import {DeviceIdMap} from '../../../shared/models/device';
import {SearchService} from '../../../core/services/search.service';
import {ButtonConfig} from '../../../shared/models/button';
import {UrlService} from '../../../core/services/url.service';
import {FilterLeaf, FilterRequest, Filters, FiltersConfig} from '../../../shared/models/filter';
import {formatTagLabel} from '../../../shared/utils/string-utils';
import {NgClass} from '@angular/common';
import {FiltersMenuComponent} from '../../components/filters-menu/filters-menu.component';
import {PillComponent} from '../../components/pill/pill.component';
import {FormsModule} from '@angular/forms';
import {CurrentWeatherComponent} from '../../components/current-weather/current-weather.component';
import {TileTagFilterPanelComponent} from '../../components/tile-tag-filter-panel/tile-tag-filter-panel.component';
import {DevicesService} from '../../../core/services/devices.service';
import {ConnectedPosition} from '@angular/cdk/overlay';
import {ClickOutsideServiceDirective} from '../../directives/click-outside-service.directive';
import {BREAKPOINT_MD, BREAKPOINT_XL} from '../../../shared/constants/responsive.constants';
import {PillListComponent} from '../../components/pill-list/pill-list.component';
import {PillItem} from '../../../shared/models/pill';

function maxVisiblePillsForWidth(w: number): number {
  if (w < BREAKPOINT_MD) return 1;
  if (w < BREAKPOINT_XL) return 2;
  return 3;
}

@Component({
  selector: 'motn-page-header',
  templateUrl: './page-header.component.html',
  styleUrls: ['./page-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    RouterLink,
    NgClass,
    FiltersMenuComponent,
    FormsModule,
    CurrentWeatherComponent,
    TileTagFilterPanelComponent,
    ClickOutsideServiceDirective,
    PillListComponent,
    PillComponent,
  ],
})
export class PageHeaderComponent implements OnInit {
  buttons: ButtonConfig[] = [];
  displayIcon: string | null;
  displayTitle: string;
  displaySubtitle: SubtitleItem[];
  weatherLocation: AppConfigLocation;
  breadcrumbs: TitleBreadcrumb[];

  searchResultQty: number = 0;
  searchEnabled: boolean = false;
  searchTerm: string = '';
  private searchTermPrevious: string = '';
  searchFocused: boolean = false;
  searchPlaceholder: string = 'Ctrl+S to search';

  filtersConfig: FiltersConfig;
  filtersOpen: boolean;
  filterPanelOpen: boolean;
  filters: Filters;
  private deviceMap: DeviceIdMap = {};

  private currentPath: string;

  lastLocationChecked: string = '';

  @Output() clickedHamburger$: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild('searchbox') searchBoxEl: ElementRef;


  @HostListener('window:keydown', ['$event'])
  onKeyPress($event: KeyboardEvent) {
    if (($event.ctrlKey || $event.metaKey) && $event.key.toLowerCase() === 's') {
      $event.preventDefault();

      if (this.searchEnabled) {
        this.focusOnSearch();
      }
    }
  }


  constructor(
    private store: Store<AppState>,
    private router: Router,
    private route: ActivatedRoute,
    private browserTitle: Title,
    private sessionService: SessionService,
    private searchService: SearchService,
    private urlService: UrlService,
    private cd: ChangeDetectorRef,
    private devicesService: DevicesService,
  ) {
  }

  filterPillItems: Array<{label: string; value: string | null; deviceId?: string; filterLabel?: string}> = [];
  maxVisiblePills = maxVisiblePillsForWidth(window.innerWidth);

  @HostListener('window:resize')
  onResize(): void {
    this.maxVisiblePills = maxVisiblePillsForWidth(window.innerWidth);
  }

  readonly pillsOverlayPositions: ConnectedPosition[] = [
    {originX: 'end', originY: 'top',    overlayX: 'end', overlayY: 'bottom', offsetY: -4},
    {originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top',    offsetY:  4},
  ];

  get filterPillListItems(): PillItem[] {
    return this.filterPillItems.map((p, i) => ({
      id: String(i),
      label: p.label,
      value: p.value,
    }));
  }

  private updateFilterPillItems(): void {
    if (!this.filters) {
      this.filterPillItems = [];
      return;
    }
    const pills: typeof this.filterPillItems = [];

    for (const id of (this.filters.devices ?? [])) {
      pills.push({label: 'Device', value: this.deviceMap[id]?.Name ?? id, deviceId: id});
    }

    if (this.filters.tagFilters?.Filters) {
      for (const node of this.filters.tagFilters.Filters) {
        if ('Key' in node && node.Key === 'tag') {
          const leaf = node as FilterLeaf;
          const displayLabel = leaf.Label ?? leaf.ID ?? '';
          pills.push({label: formatTagLabel(displayLabel), value: leaf.Value ?? null, filterLabel: leaf.ID ?? leaf.Label});
        }
      }
    }

    this.filterPillItems = pills;
  }

  removeFilterPill(item: PillItem): void {
    const pill = this.filterPillItems[parseInt(item.id, 10)];
    if (!pill) { return; }
    const f: Filters = {...this.filters};

    if (pill.deviceId) {
      f.devices = (f.devices ?? []).filter(id => id !== pill.deviceId);
    } else if (pill.filterLabel) {
      const remaining = (f.tagFilters?.Filters ?? []).filter(node => {
        if (!('Key' in node)) return true;
        const leaf = node as FilterLeaf;
        const leafId = leaf.ID ?? leaf.Label;
        return !(leafId === pill.filterLabel && (leaf.Value ?? null) === pill.value);
      });
      f.tagFilters = remaining.length ? {Operator: 'and', Filters: remaining} as FilterRequest : undefined;
    }

    this.urlService.setFilters(f);
  }


  ngOnInit(): void {
    if (navigator.platform.toLowerCase().indexOf('mac') === 0) {
      this.searchPlaceholder = this.searchPlaceholder.replace('Ctrl', '⌘');
    }

    this.store.select('title').subscribe(
      (state: TitleState) => {
        if (typeof state === 'object') {
          this.buttons = state.buttons;
          this.displayIcon = state.displayIcon;
          this.displayTitle = state.displayTitle;
          this.displaySubtitle = state.displaySubtitle;
          this.breadcrumbs = state.breadcrumbs;
          this.weatherLocation = state.weatherLocation;

          this.cd.detectChanges();

          if (this.displayTitle) {
            this.browserTitle.setTitle(`${state.displayTitle} | Motion® Platform | motion.cloud`);
          } else {
            this.browserTitle.setTitle(`Motion® Platform | motion.cloud`);
          }

          if (!this.weatherLocation) {
            this.lastLocationChecked = '';
          } else if (this.lastLocationChecked !== this.weatherLocation.PostalCode) {
            this.lastLocationChecked = this.weatherLocation.PostalCode;
          }
        }
      },
    );

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
    ).subscribe((event: NavigationEnd) => {
      const newPath: string = event.urlAfterRedirects.split('?')[0];

      // A valid path is never an empty string, but this.currentPath will
      // be empty until the first time we've had a path to fill it in with
      // so make sure it's not totally empty--otherwise the block below
      // will definitely execute, and that causes the search functionality
      // to break if the current view has search capabilities
      if (!this.currentPath) {
        this.currentPath = newPath;
      }

      // This block of code disables search when moving between routes,
      // forcing components to have to explicitly enable search if they
      // need it
      if (newPath !== this.currentPath) {
        this.searchService.disableSearch();
        this.searchService.disableFilters();
        this.searchTermPrevious = '';
        this.filtersOpen = false;
        this.filterPanelOpen = false;
        this.currentPath = newPath;
      }
    });

    /**
     * This is a hack for enabling transition-duration after the SPA has loaded
     * following user login, to prevent an immediate ugly padding transition of
     * the <main> element.
     *
     * To see the ugly effect, apply the CSS property that is being added below
     * directly in the SCSS file, and then, from a logged-out state, log in.
     */
    setTimeout(() => {
      const mainEl = document.querySelector('main');
      let transitionDuration = getComputedStyle(mainEl).getPropertyValue('--transition-duration-after-load');
      mainEl.style.setProperty('transition-duration', transitionDuration);
    }, 0);

    this.subscribeToSearchAndFilters();
  }


  private subscribeToSearchAndFilters() {
    this.devicesService.sessUserDevices$.subscribe(map => {
      this.deviceMap = map;
      this.updateFilterPillItems();
      this.cd.markForCheck();
    });

    this.urlService.filters$.subscribe({
      next: (filters) => {
        this.filters = filters;
        if (filters?.devices?.length) {
          this.devicesService.getUserDevices$().pipe(take(1)).subscribe();
        }
        this.updateFilterPillItems();
      },
    });

    this.urlService.searchTerm$.subscribe({
      next: (term) => {
        this.searchTerm = term;
      },
    });

    this.searchService.filtersEnabled$.subscribe({
      next: (enabled) => {
        this.filtersConfig = enabled;
      },
    });

    this.searchService.searchEnabled$.subscribe({
      next: (enabled) => {
        this.searchEnabled = enabled;
      },
    });

    this.searchService.searchResultQuantity$.subscribe({
      next: (qty) => {
        this.searchResultQty = qty;
      },
    });
  }


  onUserClickedFiltersButton() {
    if (this.filtersConfig) {
      this.filtersOpen = !this.filtersOpen;
    }
  }


  onSearchFocused(): void {
    if (this.filtersConfig?.by?.tags) {
      this.filterPanelOpen = true;
    }
  }


  onFilterPanelClickedOutside(): void {
    this.filterPanelOpen = false;
  }


  /**
   *
   */
  onUserClearedSearchTerm() {
    this.searchTerm = '';
    this.onUserChangedSearchTerm();
    this.focusOnSearch();
  }


  /**
   *
   */
  onUserChangedSearchTerm(): void {
    this.urlService.setSearchTerm(
      this.searchTerm,
      !!this.searchTermPrevious,
    );

    this.searchTermPrevious = this.searchTerm;
  }


  onClickHamburger(): void {
    this.clickedHamburger$.emit();
  }


  focusOnSearch(): void {
    this.searchEnabled = true;
    this.searchBoxEl?.nativeElement.focus();
    if (this.filtersConfig?.by?.tags) {
      this.filterPanelOpen = true;
    }
  }
}
